import { useApi } from "../hooks/useApi";

// ---- Local types (board endpoint only; no api/types.ts coupling) ----
interface Incident {
  key: string | null;
  summary: string | null;
  severity: string | null;          // SEV1 | SEV2 | SEV3 | null
  status: string | null;            // Jira status name
  declared_at: string | null;       // ISO
  resolved_at: string | null;       // ISO | null
  resolution_min: number | null;
  url: string | null;
}
interface BoardSummary {
  open: number;
  resolved: number;
  by_severity: Record<string, number>;
  by_status: Record<string, number>;
  mttr_minutes: number | null;
}
interface BoardResponse {
  status: string;                   // "ok" | "unavailable" | …
  reason?: string | null;
  summary: BoardSummary | null;
  incidents: Incident[];
}

const RESOLVED = new Set(["done", "resolved", "closed"]);

function sevClass(sev: string | null): string {
  switch (sev) {
    case "SEV1": return "crit";
    case "SEV2": return "warn";
    case "SEV3": return "ok";
    default:     return "muted";
  }
}

function statusClass(status: string | null): string {
  return RESOLVED.has((status ?? "").toLowerCase()) ? "ok" : "warn";
}

function ageLabel(declaredAt: string | null, resolvedAt: string | null): string {
  if (!declaredAt) return "—";
  const start = new Date(declaredAt).getTime();
  if (Number.isNaN(start)) return "—";
  const end = resolvedAt ? new Date(resolvedAt).getTime() : Date.now();
  const mins = Math.max(0, Math.round((end - start) / 60000));
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h`;
  return `${Math.round(hrs / 24)}d`;
}

function mttrLabel(min: number | null): string {
  if (min == null) return "—";
  if (min < 60) return `${Math.round(min)}m`;
  const hrs = min / 60;
  if (hrs < 48) return `${hrs.toFixed(1)}h`;
  return `${(hrs / 24).toFixed(1)}d`;
}

export function IncidentBoard() {
  const { data, loading } = useApi<BoardResponse>("/command-center/incidents/board");

  // Parent (IncidentsTab) renders its own loading/header; stay quiet while loading.
  if (loading || !data) return null;

  if (data.status !== "ok") {
    return (
      <section style={{ marginBottom: 24 }}>
        <div className="section-header"><h2>Incident board</h2></div>
        <div className="empty" style={{ textAlign: "start", padding: "20px 24px" }}>
          Connect Jira (<code>JIRA_USER</code>/<code>JIRA_TOKEN</code>) to populate the incident board.
          {data.reason && (
            <div style={{ marginTop: 4, fontSize: 11.5, color: "var(--text-muted)" }}>{data.reason}</div>
          )}
        </div>
      </section>
    );
  }

  const incidents = data.incidents ?? [];
  const s = data.summary;
  const bySev = s?.by_severity ?? {};

  return (
    <section style={{ marginBottom: 24 }}>
      <div className="section-header">
        <h2>Incident board</h2>
        <span className="count">{incidents.length}</span>
      </div>

      <div className="cov-summary">
        <span className={`cov-pill ${(s?.open ?? 0) > 0 ? "crit" : "ok"}`}>
          <span className="dot" /><strong>{s?.open ?? 0}</strong> open
        </span>
        <span className="cov-pill ok">
          <span className="dot" /><strong>{s?.resolved ?? 0}</strong> resolved
        </span>
        {bySev.SEV1 ? (
          <span className="cov-pill crit"><span className="dot" /><strong>{bySev.SEV1}</strong> SEV1</span>
        ) : null}
        {bySev.SEV2 ? (
          <span className="cov-pill warn"><span className="dot" /><strong>{bySev.SEV2}</strong> SEV2</span>
        ) : null}
        {bySev.SEV3 ? (
          <span className="cov-pill ok"><span className="dot" /><strong>{bySev.SEV3}</strong> SEV3</span>
        ) : null}
        <span className="cov-pill muted">
          <span className="dot" /><strong>{mttrLabel(s?.mttr_minutes ?? null)}</strong> MTTR
        </span>
      </div>

      {incidents.length === 0 ? (
        <div className="timeline-empty">
          No incidents declared in the selected window — nothing to triage. 🎉
        </div>
      ) : (
        <table className="cov-matrix">
          <thead>
            <tr>
              <th>Key</th>
              <th>Summary</th>
              <th>Severity</th>
              <th>Status</th>
              <th>Age</th>
            </tr>
          </thead>
          <tbody>
            {incidents.map((inc, i) => (
              <tr key={inc.key ?? i}>
                <td>
                  {inc.url && inc.key ? (
                    <a href={inc.url} target="_blank" rel="noopener"
                       style={{ fontFamily: "var(--font-mono)" }}>{inc.key}</a>
                  ) : (
                    <code style={{ fontFamily: "var(--font-mono)" }}>{inc.key ?? "—"}</code>
                  )}
                </td>
                <td>{inc.summary ?? "—"}</td>
                <td><span className={`cov-pill ${sevClass(inc.severity)}`}>{inc.severity ?? "—"}</span></td>
                <td><span className={`cov-pill ${statusClass(inc.status)}`}>{inc.status ?? "—"}</span></td>
                <td>{ageLabel(inc.declared_at, inc.resolved_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
